import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import {
  APIActions,
  Box,
  Button,
  ContentTemplate,
  CustomDialog,
  Grid,
  Icon,
  MUIDataTable,
  SectionHeader,
  TableActionTypes,
  Typography,
  useDataTable,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import RouteKey from '../../../../../routes/key';
import AccessArrangementRequestModal from './AccessArrangementRequestModal';
import {
  useDeleteAccessArrangementRequestDetails,
  useGetAccessArrangementRequestDetails,
} from '../../../../../api/candidate/access-arrangement/@candidate/queries-services/queries';
import {
  useAccessArrangementRequestColumns,
  useAccessArrangementRequestOptions,
} from '../../../logic/useAccessArrangementRequest';
import NavigationButtons from '../../../../../components/NavigationButtons';
import { candidateService } from '../../../../../api';
import DraftTimeStamp from '../../../../../components/DraftTimeStamp';

const { accessArrangement } = RouteKey.child;
const {
  ammendSelectCondition,
  root,
  selectCondition,
  individualReport,
  coordinatorRoot,
  coordinatorSelectCondition,
  coordinatorIndividualReport,
  coordinatorAmendSelectCondition,
} = RouteKey['access-arrangement'].child;

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  emptyReserve: {
    height: 314,
  },
  emptyContainer: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 77,
    paddingBottom: 77,
  },
  buttonContainer: {
    margin: theme.spacing(1),
    display: 'inline-flex',
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  buttonRemove: {
    color: theme.color.state.stateError,
  },
  icon: {
    margin: 'auto',
  },
  iconRemove: {
    margin: 'auto',
  },
  button: {
    margin: 'auto',
    padding: 10,
    minWidth: 0,
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
  },
  iconReminder: {
    marginRight: theme.spacing(1),
  },
  messageReminder: {
    fontWeight: 700,
  },
  buttonsContainer: {
    marginTop: theme.spacing(7),
    marginBottom: theme.spacing(7),
  },
  marginRight: {
    marginRight: theme.spacing(3),
  },
  errorText: {
    color: theme.color.state.stateError,
  },
}));

interface AccessArrangementRequestProps {
  amendment?: boolean;
}

interface DeleteData {
  requestType: string;
  deleteData: {
    uuid: string;
    detailUuid: string;
  };
}

// @ts-ignore
const AccessArrangementRequest = ({ amendment }: AccessArrangementRequestProps) => {
  const [isAddMode, setIsAddMode] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [accessArrangementRequestDetail, setAccessArrangementRequestDetail] = useState();
  const [deleteData, setDeleteData] = useState<DeleteData>();
  const [error, setError] = useState('');
  const [requestAmendmentArr, setRequestAmendmentArr] = useState<boolean[]>([]);
  const [dropped, setDropped] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();
  const { state, dispatch: tableDispatch } = useDataTable();
  const { classes } = useStyles();
  const [allowProceed, setAllowProceed] = useState(false);
  const {
    data: timestampData,
  } = candidateService.accessArrangement.candidate.useGetAppealArrangementDraftDateTime(uuid);
  const {
    data: accessArrangementRequestInfo,
    isLoading,
    refetch,
  } = useGetAccessArrangementRequestDetails(uuid, { order: state.order, sort: state.sortColumn });

  const deleteAccessArrangementRequestDetails = useDeleteAccessArrangementRequestDetails(() =>
    refetch()
  );
  const theme = useTheme();

  useEffect(() => {
    if (accessArrangementRequestInfo) {
      const count = accessArrangementRequestInfo.accessArrangementRequestDetails.length;

      setDropped(
        accessArrangementRequestInfo.accessArrangementRequestDetails.some((requestDetail) =>
          Object.values(requestDetail.subjectPapers).some((examLevelSubjectPapers) =>
            Object.values(examLevelSubjectPapers).some((subjectPapers) =>
              subjectPapers.some((subjectPaper) => subjectPaper.dropped)
            )
          )
        )
      );

      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: accessArrangementRequestInfo.accessArrangementRequestDetails,
          count,
        },
      } as TableActionTypes);
    }
  }, [accessArrangementRequestInfo, tableDispatch]);

  useEffect(() => {
    refetch();
  }, [refetch, state.sortColumn, state.order]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleOpenModal = (addMode = true, dataIndex?: number) => {
    setIsAddMode(addMode);

    if (!addMode && dataIndex !== undefined) {
      setAccessArrangementRequestDetail(state.data[dataIndex]);
    }

    setOpenModal(true);
    setError('');
  };

  const handleCloseModal = (doRefetch = false) => {
    if (doRefetch) {
      refetch();
    }

    setOpenModal(false);
  };

  const handleOpenDeleteModal = (data: any) => {
    setDeleteData({
      requestType: data.accessArrangementType.description,
      deleteData: {
        uuid,
        detailUuid: data.uuid,
      },
    });
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => {
    if (deleteData) {
      deleteAccessArrangementRequestDetails.mutate(deleteData.deleteData);
    }

    handleCloseDeleteModal();
  };

  const role = window.location.pathname.includes('coordinator') ? 'coordinator' : 'candidate';

  const handleBack = () => {
    if (amendment && role === 'candidate') {
      history.push(`/${accessArrangement}/${root.route}/${ammendSelectCondition.route}/${uuid}`);
      return;
    }
    if (amendment && role === 'coordinator') {
      history.push(
        `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorAmendSelectCondition.route}/${uuid}`
      );
      return;
    }
    if (role === 'candidate') {
      history.push(`/${accessArrangement}/${root.route}/${selectCondition.route}/${uuid}`);
      return;
    }
    if (role === 'coordinator') {
      history.push(
        `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorSelectCondition.route}/${uuid}`
      );
    }
  };

  let isRequestIndividualSubjectAmendment: boolean = false;
  const requestSubjectAmendmentArr: boolean[] = [];

  useEffect(
    () =>
      state.data.forEach((subject, dataIndex) => {
        const columnData: any[] = Object.values(state.data[dataIndex].subjectPapers);
        columnData.forEach((examLevelSubjectPaper: any) => {
          const examLevels: string[] = Object.keys(examLevelSubjectPaper);
          const subjectPapers: any[] = Object.values(examLevelSubjectPaper);
          examLevels.forEach((examLevel, index) => {
            isRequestIndividualSubjectAmendment = subjectPapers[index].some(
              (subjectDetail: any) => subjectDetail.isDraft === true
            );
            return requestSubjectAmendmentArr.push(isRequestIndividualSubjectAmendment);
          });
        });
        setRequestAmendmentArr(requestSubjectAmendmentArr);
      }),
    [state.data]
  );

  const isNoRequestSubjectAmendment = requestAmendmentArr.every(
    (subjectBoolean: boolean) => !subjectBoolean
  );

  const isBothStepsNoAmendment =
    amendment &&
    sessionStorage.getItem(uuid) === 'noAmendment' &&
    isNoRequestSubjectAmendment &&
    state.data.every((subject) => subject.isDraft === false);

  const handleIndividualReport = () => {
    if (!state.data.length) {
      setError(t('accessArrangement->>AccessArrangementRequest.ReviewEmptyError'));
      return;
    }
    if (role === 'candidate') {
      history.push(
        `/${accessArrangement}/${root.route}/${individualReport.route}/${uuid}`,
        { amendment: !!amendment }
      );
    }
    if (role === 'coordinator') {
      history.push(
        `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorIndividualReport.route}/${uuid}`,
        { amendment: !!amendment }
      );
    }
  };

  const tableColumns = useAccessArrangementRequestColumns(
    state,
    handleOpenModal,
    handleOpenDeleteModal,
    accessArrangementRequestInfo?.accessArrangementRequestSelections
  );

  const tableOptions = useAccessArrangementRequestOptions();

  return (
    <>
    <Prompt
      when={!allowProceed}
      message={(location) => {
        const nextPath = location.pathname;

        if (
          nextPath.includes(individualReport.route) ||
          nextPath.includes(coordinatorIndividualReport.route)
        ) {
          return true;
        }
        return t("accessArrangement->>AccessArrangements.ReminderToSubmitPopUp");
      }}
    />
    <ContentTemplate type="full">
      <Grid className={classes.headerContainer}>
        <SectionHeader title={t('accessArrangement->>StepTwo.Access Arrangements Request')} />
        <Box className={classes.buttonContainer}>
          <Icon
            size="M"
            className={classes.icon}
            name={IconName.add}
            color={theme.color.icon.iconSelected}
          />
          <Button
            id="button-accessArrangement-addRequest"
            className={classes.button}
            onClick={() => handleOpenModal()}
            variant="text"
            color="primary"
          // iconSize="L"
          >
            {t('accessArrangement->>AccessArrangementRequest.Button.AddRequest')}
          </Button>
        </Box>
      </Grid>
      <Grid>
        <MUIDataTable
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns}
          data={state.data}
          options={tableOptions}
          isLoading={isLoading}
          components={[]}
        />
      </Grid>
      {(error || dropped) && (
        <Box marginTop={theme.spacing(0.5)}>
          {(error || isBothStepsNoAmendment) && (
            <Typography className={classes.errorText} variant="body2">
              {error}
            </Typography>
          )}
          {dropped && (
            <Typography variant="body2">
              {t('accessArrangement->>AccessArrangementRequest.DroppedMessage')}
            </Typography>
          )}
        </Box>
      )}
      <NavigationButtons
        backLabel={t('accessArrangement->>AccessArrangementRequest.Button.Back')}
        onBackClick={handleBack}
        onProceedClick={handleIndividualReport}
        procceedLabel={t('accessArrangement->>AccessArrangementRequest.Button.IndividualReport')}
      />
      {timestampData && <DraftTimeStamp savedAt={timestampData} />}
      <Box display="flex" alignItems="center">
        <Icon
          size="M"
          name={IconName.info}
          className={classes.iconReminder}
          color={theme.color.icon.iconTrash}
        />
        <Typography className={classes.messageReminder} style={{color: theme.color.icon.iconTrash}}>
          {t('components->>ApplicationReminderMessage.ReminderText')}
        </Typography>
      </Box>
      {openModal && accessArrangementRequestInfo && (
        <AccessArrangementRequestModal
          isAddMode={isAddMode}
          modalState={openModal}
          onDialogClose={handleCloseModal}
          uuid={uuid}
          accessArrangementRequestSelections={
            accessArrangementRequestInfo.accessArrangementRequestSelections
          }
          accessArrangementRequestDetail={accessArrangementRequestDetail}
        />
      )}

      <CustomDialog
        open={openDeleteModal}
        title={t('accessArrangement->>AccessArrangementRequest.RemoveTitle{{requestType}}', {
          requestType: deleteData?.requestType,
        })}
        message={t('accessArrangement->>AccessArrangementRequest.RemoveMessage{{requestType}}', {
          requestType: deleteData?.requestType,
        })}
        action={
          <APIActions
            onClose={handleCloseDeleteModal}
            apiCall={handleDelete}
            cancelString={t('accessArrangement->>AccessArrangementRequest.Cancel')}
            apiString={t('accessArrangement->>AccessArrangementRequest.Remove')}
          />
        }
      />
    </ContentTemplate>
    </>
  );
};
export default AccessArrangementRequest;
